import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getUserIdeas } from '../services/ideas';
import toast from 'react-hot-toast';
import UserAvatar from '../components/UserAvatar';

const DashboardPage = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserIdeas = async () => {
      if (currentUser) {
        try {
          const userIdeas = await getUserIdeas(currentUser.uid);
          setIdeas(userIdeas);
        } catch (error) {
          console.error('Error fetching ideas:', error);
          
          // More specific error messages
          if (error.code === 'permission-denied') {
            toast.error('⚠️ Firestore permissions issue. Check FIRESTORE_SETUP.md');
          } else if (error.message?.includes('Missing or insufficient permissions')) {
            toast.error('⚠️ Please update Firestore security rules. See FIRESTORE_SETUP.md');
          } else {
            toast.error('Failed to load ideas: ' + (error.message || 'Unknown error'));
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserIdeas();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRiskBadgeColor = (risk) => {
    switch(risk) {
      case 'Low': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'High': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm border-b border-gray-200 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-32 py-4">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img 
                  src="/assets/images/EVC Logo.png" 
                  alt="Expected Value Club" 
                  className="h-28"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/ideas"
                className="text-gray-600 hover:text-primary-600 transition-colors"
              >
                Ideas
              </Link>
              <Link
                to="/leaderboard"
                className="text-gray-600 hover:text-primary-600 transition-colors"
              >
                Leaderboard
              </Link>

              <Link
                to="/about"
                className="text-gray-600 hover:text-primary-600 transition-colors"
              >
                About
              </Link>
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <Link
                to="/submit-idea"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                + Submit Idea
              </Link>
              <UserAvatar size="md" />
              <button
                onClick={handleLogout}
                className="text-gray-600 hover:text-primary-600 transition-colors"
              >
                Logout
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Onboarding Banner for Pending Users */}
        {userProfile?.onboardingStatus === 'pending' && (
          <div className="bg-gradient-to-r from-blue-600 to-primary-600 rounded-2xl shadow-lg p-8 mb-8 text-white">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold mb-2">Complete Your Onboarding</h3>
                <p className="text-blue-100 mb-4">
                  Welcome to Expected Value Club! To unlock full platform access, submit your first investment idea for review. 
                  Once approved, you'll be able to see all recent ideas and participate in discussions.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/submit-idea')}
                    className="px-6 py-3 bg-white text-primary-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                  >
                    Submit Your First Idea →
                  </button>
                  <button
                    onClick={() => navigate('/ideas')}
                    className="px-6 py-3 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-colors font-semibold"
                  >
                    Browse Ideas (Limited)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome back, {userProfile?.displayName || currentUser?.displayName || 'User'}! 👋
              </h2>
              <p className="text-gray-600 mb-4">
                {currentUser?.email}
              </p>
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">Cumulative ROI:</span>
                  <span className="text-2xl font-bold text-primary-600">
                    {userProfile?.points || 0}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">Member since:</span>
                  <span className="text-gray-900">
                    {userProfile?.createdAt 
                      ? new Date(userProfile.createdAt).toLocaleDateString('en-US', { 
                          month: 'long', 
                          year: 'numeric' 
                        })
                      : 'Recently'}
                  </span>
                </div>
              </div>
            </div>
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {(userProfile?.displayName || currentUser?.displayName || 'U').charAt(0).toUpperCase()}
            </div>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Ideas</p>
                <p className="text-3xl font-bold text-gray-900">{ideas.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Cumulative ROI</p>
                <p className="text-3xl font-bold text-green-600">+0%</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Rank</p>
                <p className="text-3xl font-bold text-purple-600">—</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Your Ideas</h3>
            <button
              onClick={() => navigate('/submit-idea')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
            >
              + New Idea
            </button>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading your ideas...</p>
            </div>
          ) : ideas.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No ideas yet</h4>
              <p className="text-gray-600 mb-6">
                Start by submitting your first prediction market idea!
              </p>
              <button 
                onClick={() => navigate('/submit-idea')}
                className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Submit Your First Idea
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {ideas.map((idea) => (
                <div 
                  key={idea.id} 
                  className="border border-gray-200 rounded-lg p-6 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/ideas/${idea.id}`)}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">{idea.title}</h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm px-2 py-1 bg-primary-100 text-primary-700 rounded-md font-medium">
                          {idea.platform}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-md font-medium ${getStatusBadgeColor(idea.status)}`}>
                          {idea.status}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-md font-medium ${getRiskBadgeColor(idea.riskLevel)}`}>
                          {idea.riskLevel} Risk
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(idea.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {idea.description}
                  </p>

                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Position</p>
                      <p className="font-semibold text-sm text-gray-900">{idea.position}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Entry</p>
                      <p className="font-semibold text-sm text-gray-900">{idea.entryPrice}¢</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Target</p>
                      <p className="font-semibold text-sm text-green-600">{idea.targetPrice}¢</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Votes</p>
                      <p className="font-semibold text-sm text-gray-900">{idea.votes || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Views</p>
                      <p className="font-semibold text-sm text-gray-900">{idea.views || 0}</p>
                    </div>
                  </div>

                  {/* Tags */}
                  {idea.tags && idea.tags.length > 0 && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                      {idea.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;

